/**
 * PlotexEngine — Wave 1 Task 2B shadow-config helpers.
 *
 * For Wave 1 this module only exposes a trio of thin helpers for enabling
 * shadow maps on the renderer, configuring the scene sun as a shadow caster,
 * and stamping castShadow/receiveShadow on every mesh under a root. Wave 3's
 * Task 3B will expand this file into a full `createPlotexEngine()` factory
 * that owns the whole runtime lifecycle; for now we keep the surface area
 * minimal so main.ts can call each helper independently.
 *
 * Why three helpers instead of one: shadow setup is naturally split in two
 * across time. The renderer-level toggle has to fire before the first render
 * pass, but the sun frustum and per-mesh flags need the scene bounding box,
 * which only exists after the GLB has loaded. Separating them lets main.ts
 * call the renderer helper at init time and the other two after the load
 * completes — without threading a "deferred init" closure through main.ts.
 */

import {
  Box3,
  type DirectionalLight,
  Object3D,
  PCFSoftShadowMap,
  Vector3,
  type WebGLRenderer,
} from 'three';

/**
 * Enable shadow map rendering on the WebGL renderer. Call once during
 * renderer setup. We pick PCFSoftShadowMap as the default because it's
 * a good quality/cost compromise for architectural scenes — crisp
 * contact shadows without the expense of VSM or the hard edges of
 * PCFShadowMap. Users can override via the options parameter.
 */
export function configureRendererShadows(
  renderer: WebGLRenderer,
  options: { enabled?: boolean; type?: typeof PCFSoftShadowMap } = {},
): void {
  renderer.shadowMap.enabled = options.enabled ?? true;
  renderer.shadowMap.type = options.type ?? PCFSoftShadowMap;
}

/**
 * Configure a DirectionalLight as a shadow caster whose frustum
 * covers the given scene bounding box.
 *
 * The frustum math: the shadow camera is an orthographic camera
 * positioned at the light's world position, looking at the center
 * of the bounds. The left/right/top/bottom are sized to encompass
 * the bounding box diagonal so no shadow-casting geometry is clipped
 * out of the frustum regardless of the light angle. Near/far are
 * clamped to the projected bbox extent plus a small margin.
 *
 * Bias: PCFSoftShadowMap needs a small negative bias to avoid shadow
 * acne. -0.0005 is a conservative default; users with polygonal
 * detail should tune this.
 */
export function configureSunShadow(
  sun: DirectionalLight,
  bounds: Box3,
  options: { mapSize?: number; bias?: number } = {},
): void {
  sun.castShadow = true;
  const mapSize = options.mapSize ?? 2048;
  sun.shadow.mapSize.set(mapSize, mapSize);
  sun.shadow.bias = options.bias ?? -0.0005;

  const center = new Vector3();
  bounds.getCenter(center);
  const size = new Vector3();
  bounds.getSize(size);
  const diagonal = size.length();
  // Orthographic frustum sized to the diagonal so any light angle
  // can cover the whole scene without clipping.
  const half = diagonal / 2;
  sun.shadow.camera.left = -half;
  sun.shadow.camera.right = half;
  sun.shadow.camera.top = half;
  sun.shadow.camera.bottom = -half;
  sun.shadow.camera.near = 0.1;
  sun.shadow.camera.far = diagonal * 2;
  // Aim the shadow camera at the scene center.
  sun.target.position.copy(center);
  sun.target.updateMatrixWorld();
  sun.shadow.camera.updateProjectionMatrix();
}

/**
 * Walk a scene and set castShadow + receiveShadow on every mesh.
 * Simple default — no per-mesh override. Task 1E adds a settings
 * field for per-project disabling; Wave 1 keeps it uniform.
 */
export function enableShadowsOnScene(root: Object3D): void {
  root.traverse((obj: any) => {
    if (obj.isMesh) {
      obj.castShadow = true;
      obj.receiveShadow = true;
    }
  });
}
